import { useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { TRY } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GitMerge, Search, ArrowRight, Folder, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { matchesSearch } from "@/lib/productIndex";

/** İki cariyi birleştir: kaynağın tüm kayıtları hedefe taşınır, kaynak çöp kutusuna gider. */
export default function ContactMergeDialog({ open, source, contacts = [], onClose, onMerged }) {
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const childCount = useMemo(() => {
    const m = {};
    contacts.forEach((c) => { if (c.parent_id) m[c.parent_id] = (m[c.parent_id] || 0) + 1; });
    return m;
  }, [contacts]);
  const candidates = useMemo(() => {
    return contacts
      .filter((c) => c.id !== source?.id && !c.is_deleted)
      .filter((c) => matchesSearch(q, c.title, c.tax_no))
      .slice(0, 30);
  }, [contacts, q, source]);

  const merge = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/merge", { source_id: source.id, target_id: target.id });
      toast.success(`'${source.title}' → '${target.title}' birleştirildi · ${data.total} kayıt taşındı`);
      onMerged && onMerged(data.target || target);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const tgtIsFolder = target && (childCount[target.id] || 0) > 0;
  const vatDiff = target && !!target.vat_exempt !== !!source?.vat_exempt;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="merge-dialog">
        <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><GitMerge className="h-6 w-6 text-primary" />Cari Birleştir</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border bg-muted/40 p-3 text-sm flex items-center gap-3 flex-wrap" data-testid="merge-summary">
            <span className="font-bold truncate max-w-[260px]" title={source?.title}>{source?.title}</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
            {target ? <span className="font-bold text-primary truncate max-w-[260px]" data-testid="merge-target-title">{target.title}</span> : <span className="text-muted-foreground italic">hedef cariyi aşağıdan seçin</span>}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            <b>{source?.title}</b> carisinin <b>tüm kayıtları</b> (cari defter, ödemeler, faturalar, teklifler, makbuzlar, irsaliyeler, iskontolar, alt cariler) hedef cariye taşınır; kaynak cari çöp kutusuna gider. Ünvan olarak hedef carinin adı kalır.
          </p>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hedef cari ara (ünvan / VKN)…" className="pl-8 h-9" autoFocus data-testid="merge-search" />
          </div>
          <div className="max-h-[300px] overflow-y-auto rounded-lg border divide-y" data-testid="merge-candidates">
            {candidates.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">Eşleşen cari yok</div>}
            {candidates.map((c) => {
              const isF = (childCount[c.id] || 0) > 0;
              const sel = target?.id === c.id;
              return (
                <button key={c.id} type="button" onClick={() => setTarget(c)} className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-muted/50 ${sel ? "bg-primary/10" : ""}`} data-testid={`merge-cand-${c.id}`}>
                  {isF && <Folder className="h-3.5 w-3.5 text-primary shrink-0" />}
                  <span className="font-semibold truncate flex-1">{c.title}</span>
                  {c.vat_exempt ? <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-700">KDV'siz</Badge> : <Badge variant="outline" className="text-[10px] border-sky-300 bg-sky-50 text-sky-700">KDV'li</Badge>}
                  <span className="text-xs tabular-nums text-muted-foreground w-24 text-right">{TRY(Math.abs(c.balance || 0))}</span>
                </button>
              );
            })}
          </div>
          {tgtIsFolder && <div className="rounded-lg border border-red-300 bg-red-50 p-2.5 text-xs text-red-800 flex gap-2" data-testid="merge-folder-warn"><AlertTriangle className="h-4 w-4 shrink-0" />Hedef bir <b>ana cari (klasör)</b>. Kayıtlar klasöre yazılmaz — klasörün içindeki bir alt cariyi hedef seçin (kaynağın kendi kaydı yoksa birleştirme yine yapılabilir).</div>}
          {vatDiff && !tgtIsFolder && <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 flex gap-2" data-testid="merge-vat-warn"><AlertTriangle className="h-4 w-4 shrink-0" />KDV durumu farklı: kaynak <b>{source?.vat_exempt ? "KDV'siz" : "KDV'li"}</b>, hedef <b>{target.vat_exempt ? "KDV'siz" : "KDV'li"}</b>. Kayıtlar olduğu gibi taşınır; hedefin KDV ayarı geçerli olur.</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>İptal</Button>
          <Button onClick={merge} disabled={!target || busy} data-testid="merge-confirm">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <GitMerge className="h-4 w-4 mr-1.5" />}Birleştir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
